import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgreementApi } from '@hero/api-hooks';
import { useCurrentUser } from '@hero/hooks-shared';
import {
  FileTextIcon,
  GlobeIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  UserIcon,
} from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { AgreementSelector } from '../../Agreement/Selector/AgreementSelector';
import { getAgreementCounterparty } from '../../Agreement/utils';
import { ClientSelector } from '../../Client/Selector/ClientSelector';
import { usePricingSheetStore } from './store/usePricingSheetStore';
import type {
  SheetScope,
  SheetScopeAgreement,
  SheetScopeDirect,
  SheetSide,
} from './types';

/**
 * Optional label overrides for the sell / buy scenario toggle. When
 * omitted, the toggle renders the generic "My selling rate" /
 * "My buying rate" copy. Surfaces with a known counterparty (the
 * per-Client Pricing tab) pass `{ sell: 'Selling price to Acme',
 * buy: 'Buying price from Acme' }` so the user can read the side
 * meaning at a glance without having to map "my buying rate" → "I am
 * the buyer here" in their head.
 */
export interface ScenarioLabels {
  sell?: string;
  buy?: string;
}

export interface PricingSheetHeaderProps {
  /**
   * When true, hide the "this price list is for…" scope picker (kind
   * toggle + customer/agreement pickers). The {@link ScenarioToggle}
   * stays visible — surfaces that hide the scope picker still need
   * the side toggle (e.g. the per-Client Pricing tab).
   */
  hideScopePicker?: boolean;
  /** Optional label overrides for the side toggle — see {@link ScenarioLabels}. */
  scenarioLabels?: ScenarioLabels;
}

/**
 * Top strip of the pricing sheet — scenario picker on the left, scope
 * picker on the right.
 *
 * Scope picker semantics (see {@link SheetScope} for the canonical model):
 *
 *   - **Everyone (own)** — open catalog price list, no counterparty. The
 *                          default workspace; what the resolver falls back
 *                          to when no directed route / agreement applies.
 *   - **A customer**     — directed pricing for a specific counterparty.
 *                          Sub-states inside this tab:
 *                            • Customer alone     → `'direct'` scope.
 *                            • Customer + agreement → `'agreement'` scope
 *                              with the agreement filter applied.
 *                          We collapse direct + agreement into a single
 *                          tab because the user almost always thinks
 *                          "pricing FOR this customer" first, and only
 *                          then optionally narrows down to "and only
 *                          under this agreement".
 *
 * Two-phase UX model:
 *   The store only ever holds **complete** scopes. While the user is
 *   choosing a counterparty (the kind is selected but the picker isn't
 *   filled yet), we keep a local `pendingKind` so the segmented control
 *   reflects the user's intent without forcing every downstream consumer
 *   to model "partial scope" states. The store commits the new scope
 *   only when the picker yields a real value; until then the page keeps
 *   listing the previous scope's rows.
 *
 * `hideScopePicker` hides the right-hand "this price list is for…"
 * cluster but **keeps** the scenario (sell/buy) toggle on the left:
 * sides remain a meaningful axis even when the scope is locked from
 * outside (e.g. the per-Client Pricing tab pre-seeds the customer and
 * exposes only the side toggle so the user can flip between
 * "selling price to this client" and "buying price from this client").
 */
export function PricingSheetHeader({
  hideScopePicker = false,
  scenarioLabels,
}: PricingSheetHeaderProps = {}) {
  const { t } = useTranslation(['m-pricing']);

  const side = usePricingSheetStore(s => s.side);
  const setSide = usePricingSheetStore(s => s.setSide);
  const scope = usePricingSheetStore(s => s.scope);
  const setScope = usePricingSheetStore(s => s.setScope);
  const lastCustomerScope = usePricingSheetStore(s => s.lastCustomerScope);
  const clearRememberedCustomer = usePricingSheetStore(
    s => s.clearRememberedCustomer,
  );

  // The UI exposes two top-level tabs (`'own'` and `'customer'`), but the
  // underlying store still uses the three-branch `SheetScope` union
  // (`'own'` / `'direct'` / `'agreement'`) because the wire model needs
  // that distinction. We collapse direct+agreement to `'customer'` for
  // the toggle and split them back out in the picker.
  const tabKind: TabKind = scope.kind === 'own' ? 'own' : 'customer';

  // Local UI intent — see "Two-phase UX model" above. Initialised from
  // the committed tab and kept in sync whenever the store's scope
  // changes from elsewhere.
  const [pendingTab, setPendingTab] = useState<TabKind>(tabKind);
  useEffect(() => {
    setPendingTab(tabKind);
  }, [tabKind]);

  const handleTabChange = (next: TabKind) => {
    if (next === pendingTab && next === tabKind) return;
    setPendingTab(next);
    // Memory-restoring toggle: when the user toggles back to "A
    // customer" after peeking at Everyone, auto-commit whatever they
    // had last time — be it a customer-only `'direct'` scope or a full
    // `'agreement'` scope. Without this the user would have to re-pick
    // the same customer every time they peek at Everyone.
    if (next === 'own') {
      setScope({ kind: 'own' });
      return;
    }
    if (next === 'customer' && lastCustomerScope) {
      setScope(lastCustomerScope);
      return;
    }
    // First-time toggle into "A customer" without a remembered value:
    // leave the committed scope alone and let the picker collect the
    // selection. The page keeps showing the previous scope's rows
    // until the picker yields a customer.
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex flex-col gap-1.5">
          <ScopeLabel
            label={t('sheet.header.scenario_label', {
              defaultValue: 'Pricing scenario',
            })}
          />
          <ScenarioToggle
            value={side}
            onChange={setSide}
            labels={scenarioLabels}
          />
        </div>

        {hideScopePicker ? null : (
          <div className="flex flex-col gap-1.5 md:items-end">
            <ScopeLabel
              label={t('sheet.header.scope_label', {
                defaultValue: 'This price list is for',
              })}
            />
            <ScopeTabToggle value={pendingTab} onChange={handleTabChange} />
          </div>
        )}
      </div>

      {hideScopePicker ? null : (
        <ScopePicker
          pendingTab={pendingTab}
          scope={scope}
          lastCustomerScope={lastCustomerScope}
          onChange={setScope}
          onClearCustomer={clearRememberedCustomer}
        />
      )}
    </div>
  );
}

/** Top-level tab in the UI — narrower than the wire-level {@link SheetScope}. */
type TabKind = 'own' | 'customer';

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function ScopeLabel({ label, icon }: { label: string; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
      {icon && <span className="text-gray-400">{icon}</span>}
      <span>{label}</span>
    </div>
  );
}

interface ScenarioToggleProps {
  value: SheetSide;
  onChange: (next: SheetSide) => void;
  /**
   * Optional sell/buy label overrides — see {@link ScenarioLabels}.
   * Each side falls back to the generic "My selling rate" / "My
   * buying rate" copy when not supplied.
   */
  labels?: ScenarioLabels;
}

function ScenarioToggle({ value, onChange, labels }: ScenarioToggleProps) {
  const { t } = useTranslation(['m-route']);

  // Side semantics are intentionally color-coded:
  //   sell → green (revenue / money in)
  //   buy  → blue  (cost / money out)
  // Red is reserved for destructive actions (delete row, error state)
  // so buy uses a calmer sky-blue. The same green/blue pair shows up
  // wherever a directed price needs side framing (RoutePriceCard,
  // pricing source badges, etc.).
  const options: Array<{
    id: SheetSide;
    title: string;
    icon: React.ReactNode;
    activeClass: string;
    inactiveClass: string;
  }> = [
    {
      id: 'sell',
      title:
        labels?.sell ??
        t('form.scenario.sell.title', { defaultValue: 'My selling rate' }),
      icon: <TrendingUpIcon className="size-3.5" />,
      activeClass:
        'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 shadow-sm',
      inactiveClass: 'text-gray-500 hover:text-emerald-700',
    },
    {
      id: 'buy',
      title:
        labels?.buy ??
        t('form.scenario.buy.title', { defaultValue: 'My buying rate' }),
      icon: <TrendingDownIcon className="size-3.5" />,
      activeClass:
        'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200 shadow-sm',
      inactiveClass: 'text-gray-500 hover:text-sky-700',
    },
  ];

  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
      {options.map(opt => {
        const isActive = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all',
              isActive ? opt.activeClass : opt.inactiveClass,
            )}>
            {opt.icon}
            {opt.title}
          </button>
        );
      })}
    </div>
  );
}

interface ScopeTabToggleProps {
  value: TabKind;
  onChange: (next: TabKind) => void;
}

/**
 * Two-way segmented control for the active "who is this for" tab. We
 * intentionally collapse `'direct'` and `'agreement'` into a single
 * "A customer" tab here — the agreement is now a sub-filter inside
 * that tab, not a sibling. Picking "A customer" without a customer
 * picked yet leaves the store on the previous scope; the table doesn't
 * change until the picker yields a value.
 */
function ScopeTabToggle({ value, onChange }: ScopeTabToggleProps) {
  const { t } = useTranslation(['m-pricing']);

  const options: Array<{
    id: TabKind;
    title: string;
    icon: React.ReactNode;
  }> = [
    {
      id: 'own',
      title: t('sheet.header.scope.own', { defaultValue: 'Everyone' }),
      icon: <GlobeIcon className="size-3.5" />,
    },
    {
      id: 'customer',
      title: t('sheet.header.scope.direct', { defaultValue: 'A customer' }),
      icon: <UserIcon className="size-3.5" />,
    },
  ];

  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
      {options.map(opt => {
        const isActive = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all',
              isActive
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700',
            )}>
            {opt.icon}
            {opt.title}
          </button>
        );
      })}
    </div>
  );
}

interface ScopePickerProps {
  /** User's UI intent — drives which picker is rendered. */
  pendingTab: TabKind;
  /** Currently committed scope from the store. */
  scope: SheetScope;
  /**
   * Last-known "A customer" scope — used to prefill the picker even
   * before the scope is committed (e.g. immediately after the user
   * toggles back to "A customer" without re-picking yet).
   */
  lastCustomerScope: SheetScopeDirect | SheetScopeAgreement | null;
  /** Commits a fully-formed scope to the store. */
  onChange: (next: SheetScope) => void;
  /** Forget the remembered customer scope (picker cleared on purpose). */
  onClearCustomer: () => void;
}

/**
 * Contextual picker rendered under the tab toggle. Branches on the
 * **pending** tab (the segmented control's value), not the committed
 * scope, so the user always sees the picker they just asked for:
 *
 *   - `'own'`      → no picker; renders a hint string instead.
 *   - `'customer'` → `CustomerSelector` (required) plus an optional
 *                    `AgreementSelector` scoped to the picked customer.
 *                    The presence of an agreement is what flips the
 *                    underlying scope from `'direct'` to `'agreement'`.
 */
function ScopePicker({
  pendingTab,
  scope,
  lastCustomerScope,
  onChange,
  onClearCustomer,
}: ScopePickerProps) {
  const { t } = useTranslation(['m-pricing']);

  if (pendingTab === 'own') {
    return (
      <div className="rounded-md bg-gray-50 px-3 py-2 text-[11px] text-gray-500">
        {t('sheet.header.own_hint', {
          defaultValue:
            'Open price list. Anyone the resolver matches to your org will see these rates as your default.',
        })}
      </div>
    );
  }

  return (
    <CustomerScopePicker
      scope={scope}
      lastCustomerScope={lastCustomerScope}
      onChange={onChange}
      onClearCustomer={onClearCustomer}
    />
  );
}

interface CustomerScopePickerProps {
  scope: SheetScope;
  lastCustomerScope: SheetScopeDirect | SheetScopeAgreement | null;
  onChange: (next: SheetScope) => void;
  onClearCustomer: () => void;
}

/**
 * Merged customer + agreement picker. The user always starts by picking
 * a customer; once a customer is selected, an optional agreement picker
 * appears underneath, scoped (by `counterpartyId`) to that customer's
 * agreements only.
 *
 * Scope mapping:
 *   - Customer alone           → `{ kind: 'direct', counterparty }`
 *   - Customer + agreement     → `{ kind: 'agreement', agreement, counterparty }`
 *
 * Side effects of user actions:
 *   - Customer cleared         → forget remembered scope, scope → `'own'`.
 *   - Customer changed         → drop the agreement (it was bound to the
 *                                previous customer), scope → `'direct'`
 *                                with the new customer.
 *   - Agreement cleared        → scope → `'direct'` with the current
 *                                customer (the customer survives the
 *                                agreement clear; only toggling tabs or
 *                                clearing the customer should lose the
 *                                customer selection).
 *   - Agreement picked         → wait for `useAgreementApi` to resolve
 *                                the parties, derive the counterparty
 *                                from `Agreement.parties[]`, commit the
 *                                full `'agreement'` scope.
 */
function CustomerScopePicker({
  scope,
  lastCustomerScope,
  onChange,
  onClearCustomer,
}: CustomerScopePickerProps) {
  const { t } = useTranslation(['m-pricing']);

  // Derive customer + agreement from the committed scope first, then
  // fall back to the remembered scope (e.g. right after a toggle back
  // to this tab but before a commit lands). This is how the picker
  // restores the user's previous customer + agreement without a
  // separate "selected" useState.
  const effective = scope.kind === 'own' ? lastCustomerScope : scope;
  const currentCustomer =
    effective?.kind === 'direct' || effective?.kind === 'agreement'
      ? effective.counterparty
      : null;
  const currentAgreementId =
    effective?.kind === 'agreement' ? effective.agreement.id : null;

  const handleCustomerChange = (
    customers: ReadonlyArray<{ _id: unknown; name: string }> | undefined,
  ) => {
    const first = customers?.[0];
    if (!first) {
      // Explicit clear → drop the remembered scope (so the next toggle
      // starts from a clean picker) and collapse to 'own'.
      onClearCustomer();
      onChange({ kind: 'own' });
      return;
    }
    const nextParty = { id: String(first._id), name: first.name };
    const isSameCustomer = currentCustomer?.id === nextParty.id;
    if (isSameCustomer) return;
    // Customer changed — the previous agreement (if any) was bound to
    // the previous customer, so drop it and commit a clean `'direct'`
    // scope. The agreement picker will reappear under the new customer
    // and the user can pick a fresh one.
    onChange({ kind: 'direct', counterparty: nextParty });
  };

  return (
    // Side-by-side layout: the agreement narrows the customer, so it
    // sits to the right of the customer selector on wide screens and
    // stacks below on narrow ones. The agreement selector is rendered
    // unconditionally and **disabled** until a customer is picked —
    // this is a deliberate UX choice so the user can always see the
    // shape of the form (two fields), with the agreement greying in
    // once it becomes pickable.
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <ScopeLabel
          label={t('sheet.header.customer_label', { defaultValue: 'Customer' })}
          icon={<UserIcon className="size-3" />}
        />
        <ClientSelector
          selectedClients={currentCustomer?.id ?? undefined}
          onClientsSelected={handleCustomerChange}
          placeholder={t('sheet.header.customer_placeholder', {
            defaultValue: 'Pick a customer',
          })}
        />
      </div>

      <AgreementScopePicker
        customer={currentCustomer}
        currentAgreementId={currentAgreementId}
        scope={scope}
        onChange={onChange}
      />
    </div>
  );
}

interface AgreementScopePickerProps {
  /**
   * Customer the agreement picker should be scoped to. `null` keeps the
   * selector visible but disabled — so the form layout stays stable
   * before a customer is picked.
   */
  customer: { id: string; name: string } | null;
  /** Currently committed agreement id (null when the scope is `'direct'`). */
  currentAgreementId: string | null;
  /** Committed scope from the store — used by the resolve-and-commit effect. */
  scope: SheetScope;
  /** Commits a fully-formed scope to the store. */
  onChange: (next: SheetScope) => void;
}

/**
 * Optional agreement narrower for the "A customer" tab. Scopes the
 * agreement dropdown to the picked customer (via `counterpartyId`), so
 * the user never sees agreements that don't apply to the current
 * customer. Renders in a disabled state when no customer is picked yet,
 * which keeps the two-field grid layout stable and signals to the user
 * that this field becomes available once they pick a customer.
 *
 * When an agreement id is picked we fetch the full Agreement via
 * `useAgreementApi` and derive the counterparty from `parties[]` — the
 * store only ever holds resolved scopes, so consumers downstream
 * (`mapRoute.ts`, the save payload) never have to know "is this
 * counterparty resolved yet?".
 *
 * Clearing the agreement collapses the scope back to `'direct'` with
 * the current customer — never to `'own'` — because the customer
 * survives the agreement clear (you're still pricing for them, just
 * not under a contract envelope).
 */
function AgreementScopePicker({
  customer,
  currentAgreementId,
  scope,
  onChange,
}: AgreementScopePickerProps) {
  const { t } = useTranslation(['m-pricing']);
  const { orgId } = useCurrentUser();

  // Local id so the selector reflects clicks immediately even before
  // `useAgreementApi` resolves the parties. Mirrors the committed
  // `currentAgreementId`; resets when the customer changes (the parent
  // re-renders us with a new `customer` / `currentAgreementId`).
  const [pendingAgreementId, setPendingAgreementId] = useState<string | null>(
    currentAgreementId,
  );
  useEffect(() => {
    setPendingAgreementId(currentAgreementId);
  }, [currentAgreementId, customer?.id]);

  const { data: agreement } = useAgreementApi(pendingAgreementId ?? undefined);

  // Once the full agreement document arrives, commit the resolved scope.
  // Falls back to `getAgreementCounterparty`'s legacy path when the
  // agreement is missing `parties[]`. Skipped when there's no customer
  // (the selector is disabled in that state so this can't fire on a
  // user action anyway, but the guard keeps the effect honest).
  useEffect(() => {
    if (!customer || !agreement || !pendingAgreementId) return;
    const cp = getAgreementCounterparty(agreement, orgId);
    if (!cp) return;
    const cpId = String(cp._id);
    const cpName = cp.name ?? customer.name;

    const alreadyCommitted =
      scope.kind === 'agreement' &&
      scope.agreement.id === pendingAgreementId &&
      scope.counterparty.id === cpId;
    if (alreadyCommitted) return;

    onChange({
      kind: 'agreement',
      agreement: { id: pendingAgreementId, label: cpName },
      counterparty: { id: cpId, name: cpName },
    });
  }, [agreement, orgId, pendingAgreementId, scope, onChange, customer]);

  const disabled = !customer;

  return (
    <div className="flex flex-col gap-1.5">
      <ScopeLabel
        label={t('sheet.header.agreement_label', { defaultValue: 'Agreement' })}
        icon={<FileTextIcon className="size-3" />}
      />
      <AgreementSelector
        value={pendingAgreementId}
        counterpartyId={customer?.id}
        counterpartyName={customer?.name}
        disabled={disabled}
        onChange={next => {
          if (!customer) return; // defensive — disabled selectors shouldn't fire
          setPendingAgreementId(next);
          if (!next) {
            // Cleared → collapse to 'direct' with the current customer.
            // The customer selection survives an agreement clear; only
            // clearing the customer itself (or toggling to Everyone)
            // drops the customer.
            onChange({ kind: 'direct', counterparty: customer });
          }
        }}
        placeholder={
          disabled
            ? t('sheet.header.agreement_placeholder_disabled', {
                defaultValue: 'Pick a customer first',
              })
            : t('sheet.header.agreement_placeholder', {
                defaultValue: 'Pick an agreement (optional)',
              })
        }
      />
    </div>
  );
}
